import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Divider, Grid, IconButton, Link, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Google } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';

import config from '@shared/config';
import { LocalizationContext } from '@cvt/contexts';
import { emailRegex } from '@cvt/helpers/validation';
import { useRouter } from '@cvt/routing';
import { SignInBox } from '@modules/Auth/components/Container/SignInBox';
import { SignUpLayout } from '@modules/Auth/components/Layout/SignUpLayout';

import { DirigoShortLogo } from '@shared/components/Logo/DirigoShortLogo';
import { useSignin } from '../hooks/useSignin';


export const Login = () => {
  const router = useRouter();

  const { dictionary } = React.useContext(LocalizationContext);
  const { control, setError, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { signIn, signInWithSSO } = useSignin();

  const onSSOLogin = React.useCallback(async (provider: string) => {
    try {
      await signInWithSSO(provider);
    } catch (e: any) {
      // @ts-ignore
      setError('email', { type: 'error', message: dictionary.auth.firebase.errors[e.error] || e.message });
    }
  }, [signInWithSSO, setError, dictionary]);

  const onSubmit = React.useCallback (async ({ email, password }: { email: string; password: string }) => {
    try {
      await signIn(email, password);
    } catch (e: any) {
      if(['auth/invalid-email','auth/user-not-found', 'auth/wrong-password'].includes(e.error)) {
        setError('email', { type: 'error', message: dictionary.auth.validations.emailOrPasswordWrong });
        setError('password', { type: 'error', message: dictionary.auth.validations.emailOrPasswordWrong });
      } else {
        setError('email', { type: 'error', message: dictionary.errors.somethingWentWrong });
      }
    }
  }, [signIn, setError, dictionary]);

  return (
    <SignUpLayout>
      <Box height="100%" display="flex" flexDirection="column" justifyContent="center">
        <Stack spacing={2} alignItems="center" mb={4} textAlign="center">
          <DirigoShortLogo height={40}/>
          <Typography variant="h1" color="buttonWhite" fontWeight={700}>Welcome Back</Typography>
          <Typography variant="h3" color="textSecondary">Sign in to continue your leadership journey</Typography>
        </Stack>
        <SignInBox>
          <Stack direction="row" justifyContent="center" spacing={2}>
            {config.featureFlags.googleSSO && (
              <Button
                color="buttonWhite"
                variant="contained"
                size="large"
                fullWidth
                startIcon={<Google/>}
                onClick={() => onSSOLogin('google')}
              >
                Continue with Google
              </Button>
            )}
            {config.featureFlags.microsoftSSO && (
              <Tooltip title={dictionary.auth.login.buttonLoginWithMicrosoft}>
                <IconButton onClick={() => onSSOLogin('microsoft')}>
                  <img src="/microsoft.svg" alt="Microsoft" width={32} height={32}/>
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Box my={2}>
            <Divider orientation="horizontal" flexItem>
              {dictionary.auth.divider}
            </Divider>
          </Box>

          <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container direction="column">
                <Grid size={12}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: dictionary.forms.validations.required,
                      pattern: {
                        value: emailRegex,
                        message: dictionary.forms.validations.invalidEmail,
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        label={dictionary.forms.fieldEmail}
                        name="email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Grid>
                <br/>
                <Grid size={12}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: dictionary.forms.validations.required }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        label={dictionary.forms.fieldPassword}
                        name="password"
                        type="password"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                      />
                    )}
                  />
                  <br/>
                  <Typography align="right">
                    <Link component={RouterLink} to={router.auth.resetPassword.path} color="primaryAlt" underline="hover" variant="body2">
                      {dictionary.auth.login.buttonResetPassword}
                    </Link>
                  </Typography>
                </Grid>
                <Grid size={12} pt={2}>
                  <LoadingButton fullWidth type="submit" loading={isSubmitting} size="large" variant="contained" color="primaryAlt">
                    {dictionary.auth.login.buttonLogin}
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </Box>
        </SignInBox>
        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="textPrimary">
            {dictionary.auth.login.dontHaveAccount}{' '}
            <Link
              component={RouterLink} to={router.auth.signup.path}
              color="primaryAlt"
              underline="hover"
            >
            Sign Up
            </Link>
          </Typography>
        </Box>
      </Box>
    </SignUpLayout>
  );
};
